/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { CmlContent } from '__generated__/graphql-types-do-not-use';

import { Typography, Typography2, breakpoints, typography2 } from '@coursera/cds-core';

import ProjectImageGallery from 'bundles/unified-description-page-common/components/modules/project-content/ProjectImageGallery';
import CML from 'bundles/unified-description-page-common/components/shared/CML';
import DottedSeparator from 'bundles/unified-description-page-common/components/shared/DottedSeparator';
import Subsection from 'bundles/unified-description-page-common/components/shared/Subsection';

import _t from 'i18n!nls/xdp';

type Props = {
  cmlTasks?: CmlContent[] | null;
  cmlRecommendedBackground?: CmlContent | null;
  screenshotUrls?: string[] | null;
};

const styles = {
  contentContainer: () => css`
    border: 2px solid var(--cds-color-grey-50);
    border-radius: 8px;
    padding: var(--cds-spacing-200) var(--cds-spacing-400) 0;
    ${breakpoints.down('xs')} {
      padding: 0 var(--cds-spacing-200);
    }
  `,
  steps: () => css`
    ${typography2.bodyPrimary}
    list-style: none;
    counter-reset: step-counter;
    margin: 0;
    padding: 0 0 var(--cds-spacing-200) 0;
  `,
  step: () => css`
    display: flex;
    padding-top: var(--cds-spacing-100);
    counter-increment: step-counter;

    ::before {
      content: counter(step-counter);
      color: var(--cds-color-blue-500);
      font-weight: bold;
    }
  `,
};

const GuidedProjectContent = ({ cmlTasks, cmlRecommendedBackground, screenshotUrls }: Props) => {
  return (
    <Subsection
      title={
        <Typography variant="h2semibold" component="h3">
          {_t('Learn step-by-step')}
        </Typography>
      }
    >
      {cmlTasks && cmlTasks.length > 0 && (
        <React.Fragment>
          <Typography2 component="p" variant="bodyPrimary">
            {_t(
              'In a video that plays in a split-screen with your work area, your instructor will walk you through these steps:'
            )}
          </Typography2>
          <ol css={styles.steps}>
            {cmlTasks.map((task, index) => {
              return (
                // eslint-disable-next-line react/no-array-index-key
                <li key={index} css={styles.step}>
                  <DottedSeparator color="var(--cds-color-blue-500)" />
                  <CML cml={task} />
                </li>
              );
            })}
          </ol>
        </React.Fragment>
      )}
      {cmlRecommendedBackground && (
        <Subsection
          title={
            <Typography2 variant="subtitleMedium" component="h4">
              {_t('Recommended experience')}
            </Typography2>
          }
          spacing={32}
        >
          <CML cml={cmlRecommendedBackground} />
        </Subsection>
      )}
      <ProjectImageGallery imageUrls={screenshotUrls} />
    </Subsection>
  );
};

export default GuidedProjectContent;
